import { appAlert } from '@/composables/useAppConfirm'
/**
 * PWA(홈 화면 앱) 설치 공통 로직
 * - 모든 주요 브라우저에서 설치 가능하도록 네이티브 프롬프트 + 단계 안내
 * - 문자 링크(?source=sms) → 즉시 「앱 설치하시겠습니까?」시트
 */
import { computed, onMounted, onUnmounted, readonly, ref, watch } from 'vue'
import { pushBackLayer, closeBackLayer } from '@/composables/useAppBackNavigation'
import { BACK_LAYER } from '@/composables/useBackCloseable'

const STORAGE_KEY = 'pwa-install-dismissed'
const INSTALL_SOURCE_KEY = 'caion_install_source'
export const PENDING_NOTIFY_AFTER_INSTALL_KEY = 'caion_pending_notify_setup'
export const SMS_INSTALL_PATH = '/entry?source=sms'

export const PWA_SW_URL = '/pwa-sw.js?v=20260819n'
const FCM_SW_URL = '/firebase-messaging-sw.js?v=20260819n'

export type BrowserKind =
  | 'inapp'
  | 'ios-safari'
  | 'ios-other'
  | 'android-chromium'
  | 'android-firefox'
  | 'android-other'
  | 'desktop'
  | 'other'

export type InstallGuide = {
  kind: BrowserKind
  label: string
  steps: string[]
  /** 네이티브 설치 버튼 사용 가능 시 기본 동작 */
  preferNative: boolean
  /** 인앱 → 외부 브라우저 유도 */
  needsExternalBrowser: boolean
  primaryLabel: string
}

const deferredPrompt = ref<any>(null)
const isInstalled = ref(false)
const canNativePrompt = ref(false)
const browserKind = ref<BrowserKind>('other')
const installGuide = ref<InstallGuide | null>(null)
const isIos = ref(false)
const isInApp = ref(false)
const fromSms = ref(false)
const showManualSteps = ref(false)
const sheetVisible = ref(false)
/** 방금 설치 완료 — 「앱으로 접속하세요」 안내 표시용 */
const installDone = ref(false)
let showTimer: ReturnType<typeof setTimeout> | null = null
let listenersBound = false
let pwaLayerBound = false

export const markPendingNotifyAfterInstall = () => {
  try {
    localStorage.setItem(PENDING_NOTIFY_AFTER_INSTALL_KEY, '1')
  } catch {
    /* ignore */
  }
}

export const clearPendingNotifyAfterInstall = () => {
  try {
    localStorage.removeItem(PENDING_NOTIFY_AFTER_INSTALL_KEY)
  } catch {
    /* ignore */
  }
}

export const hasPendingNotifyAfterInstall = (): boolean => {
  try {
    return localStorage.getItem(PENDING_NOTIFY_AFTER_INSTALL_KEY) === '1'
  } catch {
    return false
  }
}

const bindPwaBackLayer = () => {
  if (pwaLayerBound) return
  pwaLayerBound = true
  watch(sheetVisible, (open, was) => {
    if (open && !was) {
      pushBackLayer(BACK_LAYER.pwaInstall, () => {
        sheetVisible.value = false
        showManualSteps.value = false
      })
    } else if (!open && was) {
      closeBackLayer(BACK_LAYER.pwaInstall)
    }
  })
}

bindPwaBackLayer()

export const isStandaloneDisplay = () => {
  if (typeof window === 'undefined') return false
  const mql = window.matchMedia('(display-mode: standalone)').matches
  const iosStandalone = (window.navigator as any).standalone === true
  return mql || iosStandalone
}

export const detectInAppBrowser = () => {
  if (typeof window === 'undefined') return false
  const ua = window.navigator.userAgent || ''
  if (/KAKAOTALK|NAVER|FBAN|FBAV|Instagram|Line\/|DaumApps|EverytimeApp/i.test(ua)) {
    return true
  }
  if (/\bAndroid\b/i.test(ua) && /; wv\)/i.test(ua)) return true
  return false
}

const detectIos = () => {
  const ua = window.navigator.userAgent || ''
  return /iPad|iPhone|iPod/.test(ua)
}

const detectIosSafari = () => {
  const ua = window.navigator.userAgent || ''
  return detectIos() && /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo/i.test(ua)
}

/** 현재 브라우저 종류 */
export const detectBrowserKind = (): BrowserKind => {
  if (typeof window === 'undefined') return 'other'
  const ua = window.navigator.userAgent || ''

  if (detectInAppBrowser()) return 'inapp'

  if (detectIos()) {
    return detectIosSafari() ? 'ios-safari' : 'ios-other'
  }

  if (/Android/i.test(ua)) {
    if (/Firefox\//i.test(ua)) return 'android-firefox'
    // Chrome / Edge / Samsung / Whale / Opera / Naver App browser 등 Chromium
    if (
      /Chrome\//i.test(ua) ||
      /EdgA\//i.test(ua) ||
      /SamsungBrowser\//i.test(ua) ||
      /Whale\//i.test(ua) ||
      /OPR\//i.test(ua) ||
      /NAVER\(/i.test(ua)
    ) {
      return 'android-chromium'
    }
    return 'android-other'
  }

  // PC — 설치 가능하지만 모바일 앱 용도 안내
  if (/Windows|Macintosh|Linux/i.test(ua) && !/Mobile/i.test(ua)) return 'desktop'
  return 'other'
}

/** 브라우저별 설치 단계 */
export const buildInstallGuide = (kind: BrowserKind, nativeReady: boolean): InstallGuide => {
  switch (kind) {
    case 'inapp':
      return {
        kind,
        label: '인앱 브라우저',
        steps: [
          '아래 「브라우저로 열기」를 눌러 주세요',
          'Chrome 또는 Safari에서 페이지가 열리면 다시 「네, 설치합니다」를 눌러 주세요',
        ],
        preferNative: false,
        needsExternalBrowser: true,
        primaryLabel: '브라우저로 열기',
      }
    case 'ios-safari':
      return {
        kind,
        label: 'Safari',
        steps: [
          '하단(또는 상단) 공유 버튼(□↑)을 눌러 주세요',
          '목록에서 「홈 화면에 추가」또는 「앱으로 추가」를 선택해 주세요',
          '추가를 누르면 홈 화면에 카이온 앱이 생깁니다',
        ],
        preferNative: false,
        needsExternalBrowser: false,
        primaryLabel: '설치 방법 보기',
      }
    case 'ios-other':
      return {
        kind,
        label: 'iPhone 브라우저',
        steps: [
          '공유 버튼(□↑)을 눌러 주세요',
          '「홈 화면에 추가」또는 「앱으로 추가」를 선택해 주세요',
          '설치가 안 되면 Safari로 이 주소를 열어 같은 방법으로 추가해 주세요',
        ],
        preferNative: false,
        needsExternalBrowser: false,
        primaryLabel: '설치 방법 보기',
      }
    case 'android-chromium':
      return {
        kind,
        label: 'Chrome · 삼성 인터넷 · Edge 등',
        steps: nativeReady
          ? ['아래 「네, 설치합니다」를 누르면 설치 창이 열립니다', '설치를 눌러 홈 화면에 추가해 주세요']
          : [
              '주소창 오른쪽 메뉴(⋮)를 눌러 주세요',
              '「앱 설치」또는 「홈 화면에 추가」를 선택해 주세요',
              '없으면 페이지를 새로고침한 뒤 다시 시도해 주세요',
            ],
        preferNative: nativeReady,
        needsExternalBrowser: false,
        primaryLabel: nativeReady ? '네, 설치합니다' : '설치 방법 보기',
      }
    case 'android-firefox':
      return {
        kind,
        label: 'Firefox',
        steps: [
          '주소창 오른쪽 메뉴(⋮)를 눌러 주세요',
          '「홈 화면에 추가」또는 「설치」를 선택해 주세요',
        ],
        preferNative: false,
        needsExternalBrowser: false,
        primaryLabel: '설치 방법 보기',
      }
    case 'android-other':
      return {
        kind,
        label: 'Android 브라우저',
        steps: [
          '브라우저 메뉴(⋮)에서 「홈 화면에 추가」또는 「앱 설치」를 찾아 선택해 주세요',
          '없으면 Chrome으로 이 주소를 열어 설치해 주세요',
        ],
        preferNative: nativeReady,
        needsExternalBrowser: false,
        primaryLabel: nativeReady ? '네, 설치합니다' : '설치 방법 보기',
      }
    case 'desktop':
      return {
        kind,
        label: 'PC 브라우저',
        steps: [
          '주소창 오른쪽 설치 아이콘(⊕/모니터)을 눌러 주세요',
          '또는 메뉴에서 「앱 설치」를 선택해 주세요',
          '휴대폰에서 쓰시려면 문자 링크를 휴대폰으로 열어 주세요',
        ],
        preferNative: nativeReady,
        needsExternalBrowser: false,
        primaryLabel: nativeReady ? '네, 설치합니다' : '설치 방법 보기',
      }
    default:
      return {
        kind: 'other',
        label: '브라우저',
        steps: [
          '브라우저 메뉴에서 「앱 설치」또는 「홈 화면에 추가」를 선택해 주세요',
          '안 되면 Chrome 또는 Safari로 열어 주세요',
        ],
        preferNative: nativeReady,
        needsExternalBrowser: false,
        primaryLabel: nativeReady ? '네, 설치합니다' : '설치 방법 보기',
      }
  }
}

const refreshGuide = () => {
  const kind = detectBrowserKind()
  browserKind.value = kind
  isIos.value = detectIos()
  isInApp.value = kind === 'inapp'
  installGuide.value = buildInstallGuide(kind, canNativePrompt.value)
}

/**
 * URL/세션에서 문자·초대 유입 감지.
 * source=sms 로 들어오면 「나중에」기록도 지워 다시 설치 확인을 띄운다.
 */
export const captureInstallSourceFromUrl = () => {
  if (typeof window === 'undefined') return
  try {
    const params = new URLSearchParams(window.location.search)
    const src = String(params.get('source') || params.get('from') || '').toLowerCase()
    if (['sms', 'invite', 'link', 'share', 'msg', 'alim'].includes(src)) {
      sessionStorage.setItem(INSTALL_SOURCE_KEY, 'sms')
      localStorage.removeItem(STORAGE_KEY)
      fromSms.value = true
      return
    }
    fromSms.value = sessionStorage.getItem(INSTALL_SOURCE_KEY) === 'sms'
  } catch {
    fromSms.value = false
  }
}

export const isSmsInstallFlow = () => {
  if (fromSms.value) return true
  try {
    return sessionStorage.getItem(INSTALL_SOURCE_KEY) === 'sms'
  } catch {
    return false
  }
}

const refreshInstalled = () => {
  isInstalled.value = isStandaloneDisplay()
  if (isInstalled.value) {
    canNativePrompt.value = false
    sheetVisible.value = false
    showManualSteps.value = false
  }
}

const onBeforeInstall = (e: Event) => {
  e.preventDefault()
  deferredPrompt.value = e
  canNativePrompt.value = true
  refreshGuide()
  if (isSmsInstallFlow() || canAutoShowInstallSheet()) {
    scheduleAutoInstallSheet(isSmsInstallFlow() ? 80 : 600)
  }
}

const onAppInstalled = () => {
  deferredPrompt.value = null
  canNativePrompt.value = false
  showManualSteps.value = false
  isInstalled.value = true
  // 시트를 닫지 않고 「앱으로 접속하세요」 완료 안내로 전환
  installDone.value = true
  sheetVisible.value = true
  localStorage.setItem(STORAGE_KEY, String(Date.now()))
  try {
    sessionStorage.removeItem(INSTALL_SOURCE_KEY)
  } catch {
    /* ignore */
  }
  fromSms.value = false
  markPendingNotifyAfterInstall()
}

/** 백그라운드 푸시·알림 표시에 쓰는 루트 SW */
export const getPushServiceWorkerRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null
  try {
    return await navigator.serviceWorker.register(PWA_SW_URL, { scope: '/' })
  } catch (e) {
    console.warn('[pwa] push sw register failed:', e)
    try {
      return await navigator.serviceWorker.ready
    } catch {
      return null
    }
  }
}

/** 설치용 루트 SW + 구버전 FCM SW(기존 토큰 호환) */
const registerServiceWorkers = async () => {
  if (!('serviceWorker' in navigator)) return
  await getPushServiceWorkerRegistration()
  try {
    await navigator.serviceWorker.register(FCM_SW_URL, {
      scope: '/firebase-cloud-messaging-push-scope',
    })
  } catch {
    /* ignore */
  }
}

const ensureListeners = () => {
  if (listenersBound || typeof window === 'undefined') return
  listenersBound = true
  window.addEventListener('beforeinstallprompt', onBeforeInstall)
  window.addEventListener('appinstalled', onAppInstalled)
  void registerServiceWorkers()
}

export const canAutoShowInstallSheet = () => {
  if (isStandaloneDisplay()) return false
  if (isSmsInstallFlow()) return true
  if (localStorage.getItem(STORAGE_KEY)) return false
  return true
}

export const scheduleAutoInstallSheet = (delayMs = 2500) => {
  if (!canAutoShowInstallSheet()) return
  if (showTimer) clearTimeout(showTimer)
  showTimer = setTimeout(() => {
    if (canAutoShowInstallSheet()) {
      refreshGuide()
      sheetVisible.value = true
    }
  }, delayMs)
}

export const forceShowInstallSheet = () => {
  if (isStandaloneDisplay()) return
  if (showTimer) {
    clearTimeout(showTimer)
    showTimer = null
  }
  refreshGuide()
  showManualSteps.value = false
  installDone.value = false
  sheetVisible.value = true
}

export const dismissInstallSheet = () => {
  sheetVisible.value = false
  showManualSteps.value = false
  installDone.value = false
  localStorage.setItem(STORAGE_KEY, String(Date.now()))
}

export const openInstallSheet = () => {
  if (isStandaloneDisplay()) return
  refreshGuide()
  showManualSteps.value = false
  installDone.value = false
  sheetVisible.value = true
}

/** Android: Chrome 우선, 실패 시 기본 브라우저. iOS: 안내 */
export const openSystemBrowserForInstall = async () => {
  const url = `${window.location.origin}${SMS_INSTALL_PATH}`
  const ua = window.navigator.userAgent || ''

  if (/Android/i.test(ua)) {
    const hostPath = `${window.location.host}${SMS_INSTALL_PATH}`
    // Chrome → 없으면 기기 기본 브라우저
    const intent =
      `intent://${hostPath}` +
      `#Intent;scheme=https;package=com.android.chrome;` +
      `S.browser_fallback_url=${encodeURIComponent(url)};end`
    window.location.href = intent
    return
  }

  if (detectIos()) {
    showManualSteps.value = true
    await appAlert(
      '메뉴에서 「Safari로 열기」또는 「기본 브라우저로 열기」를 선택한 뒤,\n공유 → 「앱으로 추가」를 눌러 주세요.'
    )
    return
  }

  window.open(url, '_blank')
}

const runNativeInstall = async (): Promise<'accepted' | 'dismissed'> => {
  if (!deferredPrompt.value) return 'dismissed'
  deferredPrompt.value.prompt()
  const choice = await deferredPrompt.value.userChoice
  deferredPrompt.value = null
  canNativePrompt.value = false
  refreshGuide()
  if (choice?.outcome === 'accepted') {
    showManualSteps.value = false
    isInstalled.value = true
    // 「앱으로 접속하세요」 완료 안내로 전환 (시트 유지)
    installDone.value = true
    sheetVisible.value = true
    localStorage.setItem(STORAGE_KEY, String(Date.now()))
    markPendingNotifyAfterInstall()
    return 'accepted'
  }
  return 'dismissed'
}

/**
 * 설치 시도 — 네이티브 가능하면 실행, 아니면 시트에 단계 표시
 */
export const promptInstall = async (): Promise<
  'accepted' | 'dismissed' | 'manual' | 'external'
> => {
  if (isStandaloneDisplay()) {
    await appAlert('이미 앱으로 설치되어 있습니다.')
    return 'dismissed'
  }

  refreshGuide()
  const guide = installGuide.value || buildInstallGuide(detectBrowserKind(), canNativePrompt.value)

  if (guide.needsExternalBrowser) {
    await openSystemBrowserForInstall()
    return 'external'
  }

  if (canNativePrompt.value && deferredPrompt.value) {
    return runNativeInstall()
  }

  // 모든 브라우저: 단계 안내를 시트에 표시 (alert만으로 끝내지 않음)
  showManualSteps.value = true
  if (!sheetVisible.value) sheetVisible.value = true
  return 'manual'
}

export const usePwaInstall = () => {
  onMounted(() => {
    captureInstallSourceFromUrl()
    refreshInstalled()
    refreshGuide()
    fromSms.value = isSmsInstallFlow()
    if (!isInstalled.value) ensureListeners()
  })

  onUnmounted(() => {
    /* 전역 리스너 유지 */
  })

  return {
    isInstalled: readonly(isInstalled),
    canNativePrompt: readonly(canNativePrompt),
    isIos: readonly(isIos),
    isInApp: readonly(isInApp),
    fromSms: readonly(fromSms),
    browserKind: readonly(browserKind),
    installGuide: readonly(installGuide),
    showManualSteps: readonly(showManualSteps),
    installDone: readonly(installDone),
    sheetVisible,
    openInstallSheet,
    forceShowInstallSheet,
    dismissInstallSheet,
    promptInstall,
    scheduleAutoInstallSheet,
    canAutoShowInstallSheet,
    ensureListeners,
    refreshInstalled,
    openSystemBrowserForInstall,
    isSmsFlow: computed(() => isSmsInstallFlow()),
    primaryInstallLabel: computed(
      () => installGuide.value?.primaryLabel || (canNativePrompt.value ? '네, 설치합니다' : '설치 방법 보기')
    ),
  }
}

export const bootstrapPwaInstall = () => {
  captureInstallSourceFromUrl()
  refreshInstalled()
  refreshGuide()
  fromSms.value = isSmsInstallFlow()
  if (isInstalled.value) return
  ensureListeners()

  if (isSmsInstallFlow()) {
    forceShowInstallSheet()
    return
  }

  if (canAutoShowInstallSheet()) {
    scheduleAutoInstallSheet(isIos.value ? 800 : 700)
  }
}

export const teardownPwaInstallTimers = () => {
  if (showTimer) {
    clearTimeout(showTimer)
    showTimer = null
  }
}
